import { Box, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import React from 'react';

import { Maybe } from 'types/dashboard-core-gqlTypes.generated';

import { useFundingRequestContext } from '~/borrower/domains/fundingRequests/ui/manage/Context';
import { colors } from '~/packages/common-frontend/colors';
import {
  dollarNumberWithCommas,
  numberToDollarFormattedRounded,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';

const BorrowingBaseOverviewRow = ({
  label,
  value,
}: {
  label: string;
  value: Maybe<string>;
}) => {
  return (
    <Box m={4}>
      <Text color="#979797" size="sm">
        {label}
      </Text>
      <Text>{value}</Text>
    </Box>
  );
};

const FundingImpactOverviewRow = ({
  label,
  before,
  after,
}: {
  label: string;
  before: Maybe<string>;
  after: Maybe<string>;
}) => {
  return (
    <Tr>
      <Td>{label}</Td>
      <Td>{before}</Td>
      <Td>{after}</Td>
    </Tr>
  );
};

export const FundingRequestInfoBox = () => {
  const { state } = useFundingRequestContext();

  const {
    componentTotalAmounts: {
      curePaydownTotalAmount,
      collectionsPaydownTotalAmount,
      drawRequestTotalAmount,
    },
    _currentPrincipalBalance,
    _totalCash,
    _borrowingBaseAmountAssets,
    _totalBorrowingBaseAmount,
    _totalMaxAdvanceAmount,
    _borrowingBaseUtilizationPercentage,
    borrowingBaseUtilizationPercentage,
  } = state;
  const afterTotalLoanOutstanding =
    _currentPrincipalBalance +
    drawRequestTotalAmount -
    curePaydownTotalAmount -
    collectionsPaydownTotalAmount;

  return (
    <>
      {/* Should probably make this into its own component */}
      <Box
        background={colors.tableHeader}
        w="100%"
        p={4}
        color="black"
        borderBottomWidth="1px"
        borderTopWidth="1px"
      >
        Funding impact
      </Box>
      <Box margin="20px 0 20px 0">
        <Table variant="unstyled" size="sm">
          <Thead>
            <Tr>
              <Th w="40%">Measure</Th>
              <Th w="30%">Before</Th>
              <Th w="30%">After</Th>
            </Tr>
          </Thead>
          <Tbody>
            <FundingImpactOverviewRow
              label="Total loan outstanding"
              before={numberToDollarFormattedRounded(_currentPrincipalBalance)}
              after={numberToDollarFormattedRounded(afterTotalLoanOutstanding)}
            />
            <FundingImpactOverviewRow
              label="Borrowing base utilization"
              before={percentageToPercentageLabel(
                _borrowingBaseUtilizationPercentage,
                2,
              )}
              after={percentageToPercentageLabel(
                borrowingBaseUtilizationPercentage,
                2,
              )}
            />
          </Tbody>
        </Table>
      </Box>
      <Box>
        <Box
          background={colors.tableHeader}
          w="100%"
          p={4}
          color="black"
          borderBottomWidth="1px"
          borderTopWidth="1px"
        >
          Borrowing base overview
        </Box>
        <Box>
          <BorrowingBaseOverviewRow
            label="Borrowing base from receivable assets"
            value={dollarNumberWithCommas(_borrowingBaseAmountAssets)}
          />
          <BorrowingBaseOverviewRow
            label="Borrowing base from cash"
            value={dollarNumberWithCommas(_totalCash)}
          />
          <BorrowingBaseOverviewRow
            label="Total borrowing base amount"
            value={dollarNumberWithCommas(_totalBorrowingBaseAmount)}
          />
          <BorrowingBaseOverviewRow
            label="Total max advance amount"
            value={dollarNumberWithCommas(_totalMaxAdvanceAmount)}
          />
        </Box>
      </Box>
    </>
  );
};

export const FundingRequestImpactInfoBox = () => {
  return (
    <Box borderWidth="1px">
      <Box
        background={colors.tableHeader}
        w="100%"
        p={4}
        color="black"
        borderBottomWidth="1px"
      >
        Impact
      </Box>
      <Box p={4} background={colors.tableHeader}>
        <Text>Before</Text>
      </Box>
      <Table variant="unstyled">
        <Tbody>
          <Tr>
            <Td>Collections</Td>
            <Td>$850,000</Td>
          </Tr>
          <Tr>
            <Td>Loan outstanding</Td>
            <Td>$850,000</Td>
          </Tr>
        </Tbody>
      </Table>
      <Box p={4} background={colors.tableHeader}>
        <Text>After</Text>
      </Box>
      <Table variant="unstyled">
        <Tbody>
          <Tr>
            <Td>Collections</Td>
            <Td>$850,000</Td>
          </Tr>
          <Tr>
            <Td>Loan outstanding</Td>
            <Td>$850,000</Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  );
};
